// Public forms (demo + documentation requests) email the owner directly via
// Web3Forms — no backend, no database, so they work even when the (free-tier)
// Supabase project is asleep. The access key is a PUBLIC client key by design
// (submit-only, spam-filtered, rate-limited) — safe to ship in the bundle.
const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';
const WEB3FORMS_ACCESS_KEY = 'e40b3c7b-4e5c-4696-9236-a80f953a8e94';

async function sendViaWeb3Forms(fields) {
  const res = await fetch(WEB3FORMS_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ access_key: WEB3FORMS_ACCESS_KEY, from_name: 'Emboscale Landing', ...fields }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Something went wrong. Please email us directly at contact@emboscale.com.');
  }
  return data;
}

/**
 * Submit a demo request → emails the owner.
 * @param {Object} formData - { name, email, company, message? }
 */
export const submitDemoRequest = async (formData) => {
  await sendViaWeb3Forms({
    subject: `Demo request — ${formData.company || formData.name}`,
    name: formData.name,
    email: formData.email,
    company: formData.company,
    message: formData.message || '(no message)',
  });
  return { success: true, message: "Thank you! We'll contact you within 24 hours to schedule your demo." };
};

// ── Admin dashboard (still Supabase-backed) ────────────────────────────────
// Supabase is loaded LAZILY so the public landing page + forms never depend on
// it being configured or awake. Admin features need the project online.
let _supabase = null;
async function getSupabase() {
  if (!_supabase) {
    ({ supabase: _supabase } = await import('../lib/supabaseClient'));
  }
  if (!_supabase) throw new Error('Admin features need Supabase configured (REACT_APP_SUPABASE_URL / _ANON_KEY).');
  return _supabase;
}

export const getDemoRequests = async () => {
  const supabase = await getSupabase();
  const { data, error } = await supabase
    .from('demo_requests')
    .select('*')
    .order('submitted_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
};

export const getDocumentationRequests = async () => {
  const supabase = await getSupabase();
  const { data, error } = await supabase
    .from('documentation_requests')
    .select('*')
    .order('requested_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
};

export const getNotes = async (requestId) => {
  const supabase = await getSupabase();
  const { data, error } = await supabase
    .from('notes')
    .select('*')
    .eq('request_id', requestId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
};

export const createNote = async (requestId, requestType, content) => {
  const supabase = await getSupabase();
  const { data: { user }, error: uerr } = await supabase.auth.getUser();
  if (uerr || !user) throw new Error('Not authenticated');
  const { error } = await supabase.from('notes').insert([{
    request_id: requestId,
    request_type: requestType,
    admin_user_id: user.id,
    admin_email: user.email,
    content
  }]);
  if (error) throw error;
  return { success: true };
};

export const inviteAdmin = async (email, name) => {
  const supabase = await getSupabase();
  const { data, error } = await supabase.functions.invoke('admin-invite', {
    body: { email, name }
  });
  if (error) throw error;
  return data;
};

export const changePassword = async (currentPassword, newPassword) => {
  const supabase = await getSupabase();
  const { data: { user }, error: uerr } = await supabase.auth.getUser();
  if (uerr || !user) throw new Error('Not authenticated');
  const { error: reauthErr } = await supabase.auth.signInWithPassword({
    email: user.email,
    password: currentPassword
  });
  if (reauthErr) throw new Error('Current password is incorrect');
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
  return { success: true };
};
